import { useMemo } from 'react';
import { Ticket } from '@baemingo/ticket-controller';
import { LocalItem } from '~Types';
import {
  getBongData_categories,
  getBongData_operativeViewer_ticketTemplates,
} from './__queries__';
import { DateTime } from 'luxon';
import { useCurrency, useI18n, useLogger, useSelector } from '~Hooks';
import { useStorage } from '~Components/Storage';
import { sortBy } from 'lodash';
import useCheckPrinterImageSupport from '~Hooks/useCheckPrinterImageSupport';
import { combineArticles, CombinedItem } from '~Utils/combineArticles';
import { matchTicketTemplate } from './ticketTemplateUtils';
import { TicketOrderInfo } from '.';

export interface TicketElement {
  type: string;
  data?: string | null;
  height?: number;
}

export interface ProcessedTicketData {
  elements: TicketElement[];
  groupsData: ProcessedGroupData[];
  styling?: Record<string, any>; // Styling data from the template
  templateName?: string; // Name of the template used (for debugging)
}
export interface ProcessedGroupData {
  title: string;
  items: CombinedItem<LocalItem>[];
  itemsFromOtherStations: CombinedItem<LocalItem>[];
}

export interface ProcessedItemData {
  itemId: string;
  categoryId: string;
  courseType?: string;
  title: string;
  count: number;
  variations: VariationData[];
  removedVariations: VariationData[];
  comments: string[];
  price?: number;
  discount?: number;
}

export interface VariationData {
  name: string;
  price?: number;
}
interface UseTicketBuilderProps {
  items: LocalItem[];
  messages?: LocalItem[];
  categories: getBongData_categories[];
  orderInfo: TicketOrderInfo;
  ticketTemplates?: getBongData_operativeViewer_ticketTemplates[];
}

export const useTicketBuilder = ({
  items,
  messages,
  categories,
  orderInfo,
  ticketTemplates,
}: UseTicketBuilderProps) => {
  const logger = useLogger('ticket-builder');
  const printers = useSelector((state) => state.printers);
  const companyStaff = useSelector((state) => state.companyStaff?.staffs);
  const courseTypes = useSelector((state) => state.courseTypes);
  const [storage] = useStorage();
  const { i18n } = useI18n();
  const currency = useCurrency();
  const venueId = useSelector((state) => state.venueId);
  const stations = useSelector((state) => state.stations) || [];
  const { checkImagePrintingSupported } = useCheckPrinterImageSupport();

  const ticketBuilder = useMemo(() => {
    const getDataForElement = (elementType: string): string | null => {
      switch (elementType) {
        case 'PRINT_ID':
          return Math.round(Math.random() * 100).toString();
        case 'TITLE':
          return orderInfo.title;
        case 'DATE':
          return orderInfo.date
            ? DateTime.fromISO(orderInfo.date).toFormat('HH:mm yyyy-MM-dd')
            : null;
        case 'FLOORPLAN':
          return orderInfo.floorplan || null;
        case 'SERVICE_TYPE':
          if (orderInfo.serviceType && !orderInfo.tableId) {
            return !i18n(`Bong.${orderInfo.serviceType}`).includes('Missing translation')
              ? i18n(`Bong.${orderInfo.serviceType}`)
              : orderInfo.serviceType;
          }
          return null;
        case 'STAFF_NAME':
          if (orderInfo.staffId) {
            const staff = companyStaff?.find((s) => s.id === orderInfo.staffId);
            const staffName = staff?.name || orderInfo.staffId;
            return `${i18n('Bong.Waiter')}: ${staffName}`;
          }
          return null;
        case 'COVERAGE':
          return orderInfo.coverage && isFinite(orderInfo.coverage)
            ? `${i18n('Bong.Coverage')}: ${orderInfo.coverage}`
            : null;
        case 'DEVICE':
          return storage.taxComplianceDeviceName
            ? `${i18n('Bong.Device')}: ${storage.taxComplianceDeviceName}`
            : null;
        case 'DESIRED_TIME':
          return orderInfo.desiredTime
            ? `${i18n('Bong.DesiredTime')}: ${DateTime.fromISO(orderInfo.desiredTime).toFormat(
                'HH:mm yyyy-MM-dd',
              )}`
            : null;
        case 'DELIVERY_LOCATION':
          return orderInfo.deliveryLocation
            ? `${i18n('Bong.DeliveryLocation')}: ${orderInfo.deliveryLocation}`
            : null;
        case 'ORDER_COMMENT':
          return orderInfo.comment || null;
        default:
          return null;
      }
    };

    const createTicket = (ticket: Ticket): ProcessedTicketData => {
      const elements: TicketElement[] = [];

      if (!venueId) {
        logger.error('No venue ID available in state');
        return { elements: [], groupsData: [], styling: undefined, templateName: undefined };
      }

      if (!ticket || !printers) {
        logger.error('Ticket or printers data is missing', { ticket, printers });
        return { elements: [], groupsData: [], styling: undefined, templateName: undefined };
      }

      const ticketPrinter = printers.find((p) => p.id === ticket.printerId);
      const isImagePrintingSupported = checkImagePrintingSupported(ticketPrinter?.model || '');

      // Get station IDs - use ticket's stationId if available, otherwise use printer's stations
      const stationIds = ticket.stationId ? [ticket.stationId] : ticketPrinter?.stations || [];

      // Use template matching utility to get the appropriate template, structure, and styling
      const {
        structure: defaultStructure,
        styling: ticketStyling,
        template,
      } = matchTicketTemplate({
        venueId,
        stationIds,
        templates: ticketTemplates,
        isTextPrinting: !isImagePrintingSupported,
        storage,
        i18n,
        logger,
      });

      defaultStructure.forEach((structureItem: { type: string; height?: number }) => {
        const allTicketMessages = [...(ticket.messages || []), ...(messages || [])];
        if (structureItem.type === 'TICKET_MESSAGE') {
          if (allTicketMessages.length > 0) {
            allTicketMessages.forEach((ticketMessage) => {
              elements.push({
                type: structureItem.type,
                data: typeof ticketMessage === 'string' ? ticketMessage : ticketMessage.title,
              });
            });
          }
        }

        if (structureItem.type === 'STATION' && stationIds.length > 0) {
          // Use the first matching station for display
          const stationId = stationIds[0];
          const stationName = stations.find((station) => station.id === stationId)?.name;

          if (!stationName) {
            return;
          }

          elements.push({
            type: 'STATION_TITLE',
            data: `${i18n('Bong.Station')}: `,
          });
          elements.push({
            type: 'STATION_NAME',
            data: stationName,
          });
        } else {
          const data = getDataForElement(structureItem.type);

          const element = {
            type: structureItem.type,
            data,
            height: 'height' in structureItem ? structureItem.height : undefined,
          };

          if (
            element.type === 'SPACE' ||
            element.type === 'DIVIDER' ||
            element.type === 'MAIN_CONTENT' ||
            element.data !== null
          ) {
            elements.push(element);
          }
        }
      });

      const groupsData = ticket.itemGroups.map((group) => {
        const enrichedItems = group.items.map((ticketItem) => {
          const orderItem = items.find((oi) => oi.id === ticketItem.id);
          return orderItem as LocalItem;
        });

        let combinedItems = combineArticles(enrichedItems);

        // Sort items alphabetically by title if the setting is enabled
        if (storage.bongSortItemsAlphabetically) {
          combinedItems = sortBy(combinedItems, (item) => item.title.toLowerCase());
        }

        const enrichedItemsFromOtherStations = group.externalItems.map((ticketItem) => {
          const orderItem = items.find((oi) => oi.id === ticketItem.id);
          return orderItem as LocalItem;
        });

        let combinedItemsFromOtherStations = combineArticles(enrichedItemsFromOtherStations);

        // Sort items from other stations alphabetically if the setting is enabled
        if (storage.bongSortItemsAlphabetically) {
          combinedItemsFromOtherStations = sortBy(combinedItemsFromOtherStations, (item) =>
            item.title.toLowerCase(),
          );
        }

        const foundCategory = categories.find((c) => c.id === group.title);
        const foundCourseType = courseTypes?.find((c) => c.id === group.title);

        // Use the styling from the template (which already includes merged custom + default)
        const useAbbreviation = ticketStyling.GROUP_TITLE?.useAbbreviation;

        return {
          title: foundCategory
            ? i18n(foundCategory.nameLang)
            : foundCourseType
            ? useAbbreviation
              ? foundCourseType.label
              : foundCourseType.name
            : group.title || '',
          items: combinedItems,
          itemsFromOtherStations: combinedItemsFromOtherStations,
        };
      });

      return {
        elements,
        groupsData,
        styling: ticketStyling,
        templateName: template?.name,
      };
    };

    return {
      createTicket,
    };
  }, [
    orderInfo,
    i18n,
    currency.format,
    stations,
    categories,
    messages,
    items,
    companyStaff,
    storage.taxComplianceDeviceName,
    storage,
    printers,
  ]);

  return ticketBuilder;
};
